

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { DailyActivityWithAssignments, ProjectZone } from '@/types';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { Pencil, Users, Truck, CheckCircle } from 'lucide-react';
import { Textarea } from '@/shared/ui/textarea';
import { toast } from "sonner";

/**
 * マトリックスカレンダーのプロパティ
 */
interface MatrixCalendarProps {
    year: number;
    month: number;
    zones: ProjectZone[];
    activities: DailyActivityWithAssignments[];
    isReadOnly: boolean;
    onResourceEditRequest: (date: string, zoneId: string | null) => void;
    onNotesUpdate?: (date: string, zoneId: string | null, notes: string) => Promise<boolean>;
    currentUserCraftsmanId?: string | null;
}

/**
 * マトリックスカレンダー
 * @param param0 マトリックスカレンダーのプロパティ
 * @returns マトリックスカレンダー
 */
export const MatrixCalendar: React.FC<MatrixCalendarProps> = React.memo(({
    year,
    month,
    zones,
    activities,
    isReadOnly,
    onResourceEditRequest,
    onNotesUpdate,
    currentUserCraftsmanId
}) => {
    const [editingCell, setEditingCell] = useState<{ date: string; zoneId: string | null } | null>(null);
    const [editingNotes, setEditingNotes] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const editFormRef = useRef<HTMLDivElement>(null);

    const daysInMonth = useMemo(() => {
        return new Date(year, month, 0).getDate();
    }, [year, month]);

    const dates = useMemo(() => {
        return Array.from({ length: daysInMonth }, (_, i) => i + 1);
    }, [daysInMonth]);

    // 各日付のフォーマット（YYYY-MM-DD）
    const formatDateString = (day: number): string => {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    // 日付正規化の関数をメモ化
    const normalizeDateValue = useCallback((dateValue: unknown): string | null => {
        if (!dateValue) return null;

        if (typeof dateValue === 'string') {
            if (dateValue.includes('T')) {
                return dateValue.split('T')[0];
            } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            } else {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                }
            }
        }
        return null;
    }, []);

    // 最適化されたアクティビティマップ
    const activityMap = useMemo(() => {
        const map = new Map<string, DailyActivityWithAssignments>();
        const zoneIdSet = new Set(zones.map(z => String(z.id)));

        activities.forEach(activity => {
            const dateValue = activity.schedule_date;
            const dateStr = normalizeDateValue(dateValue);

            if (!dateStr) {
                return; // 無効な日付はスキップ
            }

            const zoneKey = activity.zone_id ? String(activity.zone_id) : 'all';
            const key = `${dateStr}_${zoneKey}`;

            map.set(key, activity);

            // 開発環境でのみ警告を出力（プロジェクト固有のアクティビティのみ）
            if (process.env.NODE_ENV === 'development' && activity.zone_id && !zoneIdSet.has(String(activity.zone_id))) {
                console.warn(`警告: 活動に含まれる工区ID ${activity.zone_id} はzonesリストに存在しません (プロジェクト: ${activity.project_id})`);
            }
        });

        return map;
    }, [activities, zones, normalizeDateValue]);

    /**
     * 特定の日付と工区のアクティビティを取得
     * @param date 日付
     * @param zoneId 工区ID
     * @returns アクティビティ
     */
    const getActivity = useCallback((date: string, zoneId: string | null): DailyActivityWithAssignments | undefined => {
        const key = `${date}_${zoneId ? String(zoneId) : 'all'}`;
        const activity = activityMap.get(key);

        return activity;
    }, [activityMap]);

    /**
     * 工区×日付セルのクリックハンドラー
     * @param date 日付
     * @param zoneId 工区ID
     */
    const handleCellClick = (date: string, zoneId: string | null) => {
        if (!isReadOnly) {
            onResourceEditRequest(date, zoneId);
        }
    };

    /**
     * メモ編集モードを開始
     * @param date 日付
     * @param zoneId 工区ID
     * @param currentNotes 現在のメモ
     */
    const handleEditNotes = useCallback((date: string, zoneId: string | null, currentNotes: string = '') => {
        if (isReadOnly) return;

        setEditingCell({ date, zoneId });
        setEditingNotes(currentNotes || '');
    }, [isReadOnly]);

    /**
     * メモ編集をキャンセル
     */
    const handleCancelEdit = useCallback(() => {
        setEditingCell(null);
        setEditingNotes('');
    }, []);

    /**
     * メモを保存
     */
    const handleSaveNotes = useCallback(async () => {
        if (!editingCell || !onNotesUpdate) return;

        setIsSaving(true);

        try {
            // 現在編集中のセルのアクティビティを取得
            const activity = getActivity(editingCell.date, editingCell.zoneId);

            // 保存処理を実行
            const success = await onNotesUpdate(
                editingCell.date,
                editingCell.zoneId,
                editingNotes
            );

            if (success) {
                toast.success("メモを保存しました");
                // 編集モードを終了
                setEditingCell(null);
                // フォーカスを外す
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
            } else {
                toast.error("メモの保存に失敗しました");
            }
        } catch (error) {
            console.error('Failed to save notes:', error);
            toast.error("エラー", {
                description: error instanceof Error
                    ? `保存処理中にエラーが発生しました: ${error.message}`
                    : '保存処理中に不明なエラーが発生しました'
            });
        } finally {
            setIsSaving(false);
        }
    }, [editingCell, editingNotes, onNotesUpdate, getActivity]);

    /**
     * 編集フォーム外をクリックした時の処理
     */
    useEffect(() => {
        if (!editingCell) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                editFormRef.current &&
                !editFormRef.current.contains(event.target as Node)
            ) {
                // 変更があるかどうかをチェック
                const activity = getActivity(editingCell.date, editingCell.zoneId);
                const originalNotes = activity?.notes || '';

                if (editingNotes !== originalNotes) {
                    // 変更がある場合は確認メッセージを表示
                    if (confirm('変更が保存されていません。保存しますか？')) {
                        handleSaveNotes();
                    } else {
                        handleCancelEdit();
                    }
                } else {
                    // 変更がない場合は直接キャンセル
                    handleCancelEdit();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editingCell, editingNotes, handleSaveNotes, handleCancelEdit]);

    // フォーカス制御
    useEffect(() => {
        if (editingCell && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [editingCell, getActivity]);

    /**
     * セルに割り当てられたリソースに自分がいるかどうか判定する関数
     * @param activity アクティビティ
     * @returns 自分がいるかどうか
     */
    const isCurrentUserAssigned = useCallback((activity: DailyActivityWithAssignments | undefined): boolean => {
        if (!activity || !currentUserCraftsmanId) return false;

        // 職人割り当て配列から自分のIDが含まれているか確認
        return activity.craftsman_assignments?.some(assignment =>
            assignment.craftsman_id === currentUserCraftsmanId
        ) || false;
    }, [currentUserCraftsmanId]);

    // 割り当てサマリーを取得（職人数・トラック数・現在ユーザーが割り当て済みか）
    const getAssignmentsSummary = (activity?: DailyActivityWithAssignments) => {

        const craftsmenCount = activity?.craftsman_assignments?.length || 0;
        const trucksCount = activity?.truck_assignments?.length || 0;

        // 現在のユーザーが職人かどうかを確認（職人IDがセットされている場合のみ）
        const isCurrentUserAssigned =
            !!currentUserCraftsmanId &&
            !!activity?.craftsman_assignments?.some(
                assignment => assignment.craftsman_id === currentUserCraftsmanId
            );

        return {
            craftsmenCount,
            trucksCount,
            isCurrentUserAssigned
        };
    };

    /**
     * アクティビティ詳細ツールチップの内容
     * @param activity アクティビティ
     * @returns ツールチップの内容
     */
    const renderActivityTooltip = (activity: DailyActivityWithAssignments | undefined) => {
        if (!activity) return 'データなし';

        const { craftsman_assignments, truck_assignments } = activity;

        return (
            <div className="max-w-xs space-y-2">
                {activity.notes && (
                    <div>
                        <div className="font-semibold mb-1">メモ:</div>
                        <div className="text-sm">{activity.notes}</div>
                    </div>
                )}

                {craftsman_assignments && craftsman_assignments.length > 0 && (
                    <div>
                        <div className="font-semibold mb-1">割り当て職人:</div>
                        <ul className="text-sm space-y-1">
                            {craftsman_assignments.map(assignment => (
                                <li key={assignment.id} className="flex flex-col">
                                    <span className="font-medium">職人ID: {assignment.craftsman_id}</span>
                                    {assignment.vehicle_id && (
                                        <span className="text-xs text-muted-foreground ml-2">
                                            [VEHICLE] 車両ID: {assignment.vehicle_id.substring(0, 8)}...
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {truck_assignments && truck_assignments.length > 0 && (
                    <div>
                        <div className="font-semibold mb-1">割り当てトラック:</div>
                        <ul className="text-sm space-y-1">
                            {truck_assignments.map(assignment => (
                                <li key={assignment.id} className="flex flex-col">
                                    <span className="font-medium">トラックID: {assignment.truck_id}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                        [DRIVER] 運転手ID: {assignment.driver_id}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {!activity.notes && (!craftsman_assignments || craftsman_assignments.length === 0) &&
                    (!truck_assignments || truck_assignments.length === 0) && (
                        <div className="text-sm italic">情報がありません</div>
                    )}
            </div>
        );
    };

    // 工区に関する検証
    useEffect(() => {
        // ユニークなゾーンIDをactivitiesから抽出（プロジェクト固有のアクティビティのみ）
        const uniqueZoneIds = [...new Set(activities
            .filter(a => a.zone_id)
            .map(a => a.zone_id))];

        // 工区リストに存在しないゾーンIDを検出
        const missingZoneIds = uniqueZoneIds.filter(id => !zones.some(z => z.id === id));
        if (missingZoneIds.length > 0) {
            console.warn(`警告: 以下のzone_idがactivitiesに含まれますが、工区リストにありません: ${missingZoneIds.join(', ')} (プロジェクト固有のアクティビティのみ)`);
        }
    }, [zones, activities]);

    return (
        <TooltipProvider>
            <div className="overflow-x-auto overflow-y-auto max-h-[75vh] bg-white rounded-md">
                <table className="min-w-full border-collapse">
                    <thead>
                        <tr>
                            {/* 左上の空白セル */}
                            <th className="sticky left-0 top-0 z-30 w-16 sm:w-24 md:min-w-[120px] bg-white border-b p-0.5 sm:p-2 text-left font-medium text-muted-foreground shadow-md text-xs sm:text-sm">
                                <span className="hidden sm:inline">工区 / 日付</span>
                                <span className="sm:hidden">工区</span>
                            </th>

                            {/* 日付ヘッダー */}
                            {dates.map(day => (
                                <th
                                    key={`header-${day}`}
                                    className="sticky top-0 z-20 bg-white border-b p-0.5 sm:p-2 text-center text-xs sm:text-sm font-medium text-muted-foreground min-w-[60px] sm:min-w-[80px] shadow-sm"
                                >
                                    <span className="hidden sm:inline">{day}日</span>
                                    <span className="sm:hidden">{day}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {/* 全体行（zone_id: null）*/}
                        <tr className="bg-muted/10 hover:bg-muted/20">
                            <td className="sticky left-0 z-10 bg-white p-0.5 sm:p-2 font-medium border-b shadow-sm text-xs sm:text-sm">
                                全体
                            </td>

                            {dates.map(day => {
                                const dateStr = formatDateString(day);
                                const activity = getActivity(dateStr, null);
                                const hasNotes = activity?.notes && activity.notes.trim().length > 0;
                                const isEditing = editingCell?.date === dateStr && editingCell?.zoneId === null;
                                const userAssigned = isCurrentUserAssigned(activity);

                                return (
                                    <td
                                        key={`all-${day}`}
                                        className={cn(
                                            "border text-center p-0.5 relative",
                                            hasNotes ? "bg-blue-50 dark:bg-blue-950/20" : "",
                                            userAssigned ? "bg-emerald-100 dark:bg-emerald-950/30 ring-2 ring-emerald-300 dark:ring-emerald-700" : "",
                                            isEditing ? "p-0" : "cursor-pointer hover:bg-muted/40 transition-colors"
                                        )}
                                        onClick={isEditing ? undefined : () => handleEditNotes(dateStr, null, activity?.notes || '')}
                                    >
                                        {isEditing ? (
                                            <div className="p-1" ref={editFormRef}>
                                                <Textarea
                                                    ref={textareaRef}
                                                    value={editingNotes}
                                                    onChange={(e) => setEditingNotes(e.target.value)}
                                                    className="min-h-[60px] text-sm resize-none"
                                                    placeholder="メモを入力..."
                                                    autoFocus
                                                    disabled={isSaving}
                                                />
                                                <div className="flex justify-end space-x-1 mt-1">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleCancelEdit}
                                                        disabled={isSaving}
                                                        className="h-6 text-xs"
                                                    >
                                                        キャンセル
                                                    </Button>
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={handleSaveNotes}
                                                        disabled={isSaving}
                                                        className="h-6 text-xs"
                                                    >
                                                        {isSaving ? (
                                                            <>保存中...</>
                                                        ) : (
                                                            <>
                                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                                保存
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : hasNotes ? (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className="text-xs text-left p-1 overflow-hidden text-ellipsis max-h-10 line-clamp-2 font-normal">
                                                        {activity.notes}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <div>
                                                        <div className="font-semibold mb-1">全体メモ:</div>
                                                        <div className="max-w-xs">{activity.notes}</div>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        ) : !isReadOnly ? (
                                            <div className="text-xs text-muted-foreground opacity-50 hover:opacity-100">
                                                <Pencil className="h-3 w-3 mx-auto" />
                                            </div>
                                        ) : null}
                                    </td>
                                );
                            })}
                        </tr>

                        {/* 工区ごとの行 */}
                        {zones.map(zone => (
                            <tr key={`zone-${zone.id}`} className="hover:bg-muted/10">
                                <td className="sticky left-0 z-10 bg-white p-0.5 sm:p-2 font-medium border-b shadow-sm text-xs sm:text-sm">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="truncate max-w-full">
                                                <span className="hidden sm:inline">{zone.zone_name || '未設定'}</span>
                                                <span className="sm:hidden">
                                                    {zone.zone_name && zone.zone_name.length > 8
                                                        ? `${zone.zone_name.substring(0, 6)}...`
                                                        : zone.zone_name || '未設定'}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <div className="max-w-xs">
                                                <div className="font-semibold">{zone.zone_name}</div>
                                                {zone.notes && (
                                                    <div className="text-sm text-muted-foreground mt-1">{zone.notes}</div>
                                                )}
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                </td>

                                {dates.map(day => {
                                    const dateStr = formatDateString(day);
                                    const activity = getActivity(dateStr, zone.id);
                                    const { craftsmenCount, trucksCount, isCurrentUserAssigned } = getAssignmentsSummary(activity);
                                    const hasAssignments = craftsmenCount > 0 || trucksCount > 0;
                                    const hasNotes = activity?.notes && activity.notes.trim().length > 0;

                                    return (
                                        <td
                                            key={`${zone.id}-${day}`}
                                            className={cn(
                                                "border text-center p-0.5 cursor-pointer hover:bg-muted/40 transition-colors relative",
                                                hasAssignments ? "bg-green-50 dark:bg-green-950/20" : "",
                                                hasNotes ? "bg-blue-50 dark:bg-blue-950/20" : "",
                                                isCurrentUserAssigned ? "bg-emerald-100 dark:bg-emerald-950/30 ring-2 ring-emerald-300 dark:ring-emerald-700" : ""
                                            )}
                                            onClick={() => handleCellClick(dateStr, zone.id)}
                                        >
                                            {/* 工区専用メモの表示 */}
                                            {hasNotes && (
                                                <div className="mb-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div className="text-xs text-left p-1 overflow-hidden text-ellipsis max-h-8 line-clamp-1 font-normal bg-blue-100 dark:bg-blue-900/50 rounded border">
                                                                {activity.notes}
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <div>
                                                                <div className="font-semibold mb-1">メモ:</div>
                                                                <div className="max-w-xs">{activity.notes}</div>
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            )}

                                            {/* 割り当て表示部分 */}
                                            {craftsmenCount > 0 || trucksCount > 0 ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="flex justify-center items-center space-x-1">
                                                            {craftsmenCount > 0 && (
                                                                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-xs py-0">
                                                                    <Users className="h-3 w-3 mr-1" />
                                                                    {craftsmenCount}
                                                                </Badge>
                                                            )}

                                                            {trucksCount > 0 && (
                                                                <Badge variant="outline" className="bg-orange-50 dark:bg-orange-900/30 text-xs py-0">
                                                                    <Truck className="h-3 w-3 mr-1" />
                                                                    {trucksCount}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {renderActivityTooltip(activity)}
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : null}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </TooltipProvider>
    );
}); 